import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Car, Home, Loader2, ShieldCheck, CheckCircle2, ChevronDown } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";

const EXPENSE_LABELS = {
    rates: "Council rates",
    mortgage_interest: "Mortgage interest",
    rent: "Rent",
    power: "Power / electricity",
    gas: "Gas",
    water: "Water",
    insurance: "Home / contents insurance",
    internet: "Internet",
    phone: "Phone",
    repairs_maintenance: "Repairs & maintenance",
    other: "Other (specify in notes)",
};

const HO_METHOD_LABELS = {
    actual_costs: "Itemise actual costs",
    square_metre_rate: "IRD square metre rate (utilities) + premises costs",
};

const HO_METHOD_DESCRIPTIONS = {
    actual_costs: "List every category (rates, power, internet, etc.) with the annual amount.",
    square_metre_rate: "IRD publishes a $/m² rate yearly that covers utilities (power, gas, water, internet, phone, insurance, repairs). Rates / mortgage interest / rent are claimed separately.",
};

const METHOD_LABELS = {
    logbook: "Logbook (90-day record)",
    km_rate: "IRD km rate (Tier 1/Tier 2)",
    actual_costs: "Actual vehicle costs",
};

const blankVehicle = () => ({
    registration: "",
    make: "",
    model: "",
    business_use_percent: 0,
    method: "km_rate",
    total_km_annual: "",
    business_km_annual: "",
    logbook_period_start: "",
    logbook_period_end: "",
    notes: "",
});

export default function PortalCompliance() {
    const { activeClient, activeClientId, user } = useAuth();
    const [record, setRecord] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const load = async (cid) => {
        if (!cid) return;
        setLoading(true);
        try {
            const [m, r] = await Promise.all([
                api.get("/compliance/meta"),
                api.get(`/compliance/current?client_id=${cid}`),
            ]);
            setMeta(m.data);
            setRecord(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(activeClientId); /* eslint-disable-line */ }, [activeClientId]);

    const ho = record?.home_office || {};
    const mv = record?.motor_vehicle || {};
    const isReadOnly = record && !(record.status === "draft" || record.status === "needs_changes");

    const computedBusPct = useMemo(() => {
        const total = Number(ho.total_home_area_sqm || 0);
        const office = Number(ho.office_area_sqm || 0);
        if (total > 0 && office > 0) return Math.round((office / total) * 1000) / 10;
        return null;
    }, [ho.total_home_area_sqm, ho.office_area_sqm]);

    const overLogbookThreshold = (v) => Number(v.business_use_percent || 0) > (meta?.logbook_threshold_percent || 25);

    const logbookGap = useMemo(() => {
        if (!mv.has_vehicle_claim) return null;
        for (const v of (mv.vehicles || [])) {
            if (Number(v.business_use_percent || 0) > (meta?.logbook_threshold_percent || 25)
                && (v.method || "km_rate") === "logbook"
                && (!v.logbook_period_start || !v.logbook_period_end)) {
                return `Vehicle ${v.registration || "(no rego)"} — logbook period dates required`;
            }
        }
        return null;
    }, [mv, meta]);

    function patchHomeOffice(partial) {
        setRecord((r) => ({ ...r, home_office: { ...(r.home_office || {}), ...partial } }));
    }
    function patchMotor(partial) {
        setRecord((r) => ({ ...r, motor_vehicle: { ...(r.motor_vehicle || {}), ...partial } }));
    }
    function patchVehicle(idx, partial) {
        setRecord((r) => {
            const vs = [...(r.motor_vehicle?.vehicles || [])];
            vs[idx] = { ...vs[idx], ...partial };
            return { ...r, motor_vehicle: { ...(r.motor_vehicle || {}), vehicles: vs } };
        });
    }
    function addVehicle() {
        patchMotor({ vehicles: [...(mv.vehicles || []), blankVehicle()] });
    }
    function removeVehicle(idx) {
        const vs = (mv.vehicles || []).filter((_, i) => i !== idx);
        patchMotor({ vehicles: vs });
    }

    async function save() {
        setSaving(true);
        try {
            const body = {
                home_office: ho,
                motor_vehicle: mv,
                declaration_accepted: !!record.declaration_accepted,
                declaration_name: record.declaration_name,
            };
            const { data } = await api.put(`/compliance/${record.id}`, body);
            setRecord(data);
            toast.success("Saved as draft");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSaving(false); }
    }

    async function submitRecord() {
        if (!record.declaration_accepted) {
            toast.error("Please tick the declaration to confirm the information is accurate");
            return;
        }
        setSubmitting(true);
        try {
            // Save first
            await api.put(`/compliance/${record.id}`, {
                home_office: ho,
                motor_vehicle: mv,
                declaration_accepted: !!record.declaration_accepted,
                declaration_name: record.declaration_name,
            });
            const { data } = await api.post(`/compliance/${record.id}/submit`);
            setRecord(data);
            toast.success("Submitted — your accountant has been notified");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSubmitting(false); }
    }

    if (loading || !record) {
        return <div className="flex items-center gap-2 text-sm text-[#5E636E] py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading compliance record…</div>;
    }

    return (
        <div className="space-y-6" data-testid="portal-compliance-page">
            <div>
                <div className="co-label">Year-end compliance</div>
                <h1 className="font-display text-3xl tracking-tight font-medium mt-1">IRD compliance — {record.tax_year}</h1>
                <p className="text-sm text-[#5E636E] mt-2">
                    {activeClient?.name} · NZ tax year 1 Apr {record.tax_year.split("-")[0]} → 31 Mar {record.tax_year.split("-")[1]}
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <StatusBadge status={record.status} testId="compliance-status" />
                    {record.status === "submitted" && (
                        <span className="text-xs text-[#5E636E]">Submitted {new Date(record.submitted_at).toLocaleDateString()}</span>
                    )}
                    {record.status === "accepted" && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Accepted by your accountant</span>
                    )}
                </div>
            </div>

            {record.status === "needs_changes" && record.reviewer_notes && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-4" data-testid="compliance-needs-changes-banner">
                    <div className="text-sm font-medium text-amber-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Your accountant has asked for changes</div>
                    <div className="mt-1 text-sm text-amber-900 whitespace-pre-wrap">{record.reviewer_notes}</div>
                </div>
            )}

            {/* HOME OFFICE */}
            <div className="co-card co-card-pad" data-testid="ho-section">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 grid place-items-center rounded-full bg-emerald-50 text-emerald-700"><Home className="h-4.5 w-4.5" /></div>
                        <div>
                            <div className="font-display text-lg font-medium">Home office claim</div>
                            <div className="text-xs text-[#5E636E]">IRD IS 19/05 — percentage of home used for business</div>
                        </div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                            checked={!!ho.has_home_office}
                            onCheckedChange={(v) => patchHomeOffice({ has_home_office: !!v })}
                            disabled={isReadOnly}
                            data-testid="ho-toggle"
                        />
                        I&apos;m claiming home office expenses
                    </label>
                </div>

                {ho.has_home_office && (
                    <div className="mt-5 space-y-4">
                        {/* Method picker */}
                        <div>
                            <Label className="co-label">Calculation method</Label>
                            <div className="mt-2 grid sm:grid-cols-2 gap-2">
                                {(meta?.home_office_methods || ["actual_costs", "square_metre_rate"]).map((m) => (
                                    <button
                                        type="button"
                                        key={m}
                                        onClick={() => !isReadOnly && patchHomeOffice({ method: m })}
                                        disabled={isReadOnly}
                                        className={`text-left rounded-lg border p-3 transition-colors ${
                                            (ho.method || "actual_costs") === m
                                                ? "border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-300"
                                                : "border-[#E4E4E7] bg-white hover:bg-zinc-50"
                                        }`}
                                        data-testid={`ho-method-${m}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className={`h-3.5 w-3.5 rounded-full border ${(ho.method || "actual_costs") === m ? "bg-emerald-500 border-emerald-500" : "border-[#5E636E]"}`}></span>
                                            <div className="text-sm font-medium">{HO_METHOD_LABELS[m] || m}</div>
                                        </div>
                                        <div className="mt-1 text-[11px] text-[#5E636E] pl-5">{HO_METHOD_DESCRIPTIONS[m]}</div>
                                    </button>
                                ))}
                            </div>
                            {meta?.ird_square_metre_rate && (ho.method || "actual_costs") === "square_metre_rate" && (
                                <p className="mt-2 text-[11px] text-[#5E636E]" data-testid="ho-sqm-rate-note">
                                    IRD square metre rate in use: <strong>${meta.ird_square_metre_rate.toFixed(2)} / m²</strong> (published for {meta.ird_square_metre_rate_year}).{" "}
                                    <a className="underline" href="https://www.ird.govt.nz/income-tax/income-tax-for-businesses-and-organisations/types-of-business-expenses/using-your-home-for-your-business" target="_blank" rel="noreferrer">IRD reference</a>
                                </p>
                            )}
                        </div>

                        <div className="grid sm:grid-cols-3 gap-3">
                            <div>
                                <Label className="co-label">Total home area (m²)</Label>
                                <Input
                                    type="number" inputMode="decimal" min="0" step="0.1"
                                    value={ho.total_home_area_sqm ?? ""}
                                    onChange={(e) => patchHomeOffice({ total_home_area_sqm: e.target.value === "" ? null : Number(e.target.value) })}
                                    disabled={isReadOnly}
                                    data-testid="ho-total-area"
                                />
                            </div>
                            <div>
                                <Label className="co-label">Office area (m²)</Label>
                                <Input
                                    type="number" inputMode="decimal" min="0" step="0.1"
                                    value={ho.office_area_sqm ?? ""}
                                    onChange={(e) => patchHomeOffice({ office_area_sqm: e.target.value === "" ? null : Number(e.target.value) })}
                                    disabled={isReadOnly}
                                    data-testid="ho-office-area"
                                />
                            </div>
                            <div>
                                <Label className="co-label">Business use %</Label>
                                <div className="mt-2 rounded-md border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2 text-sm font-mono" data-testid="ho-pct">
                                    {computedBusPct != null ? `${computedBusPct}%` : "—"}
                                </div>
                            </div>
                        </div>

                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                                checked={!!ho.is_dedicated_space}
                                onCheckedChange={(v) => patchHomeOffice({ is_dedicated_space: !!v })}
                                disabled={isReadOnly}
                                data-testid="ho-dedicated"
                            />
                            This space is used <strong>exclusively</strong> for business
                        </label>

                        <div>
                            <Label className="co-label">
                                {(ho.method || "actual_costs") === "square_metre_rate"
                                    ? "Premises costs (claimed separately at business-use %)"
                                    : "Expenses claimed"}
                            </Label>
                            {(ho.method || "actual_costs") === "square_metre_rate" && (
                                <p className="text-[11px] text-[#5E636E] mt-1">
                                    Utilities (power, gas, water, internet, phone, insurance, repairs) are <strong>already covered</strong> by the IRD square metre rate.
                                    Only enter rates / mortgage interest / rent here.
                                </p>
                            )}
                            <div className="mt-2 grid sm:grid-cols-2 gap-2">
                                {(() => {
                                    const allCats = meta?.expense_categories || [];
                                    const cats = (ho.method || "actual_costs") === "square_metre_rate"
                                        ? allCats.filter((c) => (meta?.premises_categories || ["rates", "mortgage_interest", "rent"]).includes(c))
                                        : allCats;
                                    return cats.map((cat) => {
                                        const checked = (ho.expense_categories || []).includes(cat);
                                        return (
                                            <div key={cat} className="rounded-md border border-[#E4E4E7] bg-white px-3 py-2 flex items-center gap-3" data-testid={`ho-expense-${cat}`}>
                                                <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={(v) => {
                                                        const cur = new Set(ho.expense_categories || []);
                                                        if (v) cur.add(cat); else cur.delete(cat);
                                                        patchHomeOffice({ expense_categories: Array.from(cur) });
                                                    }}
                                                    disabled={isReadOnly}
                                                />
                                                <div className="flex-1 text-sm">{EXPENSE_LABELS[cat] || cat}</div>
                                                {checked && (
                                                    <Input
                                                        type="number" min="0" step="0.01"
                                                        placeholder="Annual $"
                                                        value={ho.annual_expenses?.[cat] ?? ""}
                                                        onChange={(e) => patchHomeOffice({
                                                            annual_expenses: {
                                                                ...(ho.annual_expenses || {}),
                                                                [cat]: e.target.value === "" ? null : Number(e.target.value),
                                                            },
                                                        })}
                                                        className="w-28 text-right"
                                                        disabled={isReadOnly}
                                                        data-testid={`ho-amount-${cat}`}
                                                    />
                                                )}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>

                        {(ho.method || "actual_costs") === "square_metre_rate" && Number(ho.office_area_sqm || 0) > 0 && meta?.ird_square_metre_rate && (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="ho-sqm-summary">
                                <div className="co-label text-emerald-800">IRD square metre rate — utilities</div>
                                <div className="mt-1 text-sm text-emerald-900">
                                    {Number(ho.office_area_sqm).toFixed(1)} m² × ${meta.ird_square_metre_rate.toFixed(2)}
                                    {" "}= <strong data-testid="ho-sqm-utilities-total">${(Number(ho.office_area_sqm) * meta.ird_square_metre_rate).toFixed(2)}</strong>
                                    {" "}covers utilities for the year.
                                </div>
                                <div className="mt-1 text-[11px] text-emerald-800">
                                    Add this to your business-use % of the premises costs entered above to get the total home-office claim.
                                </div>
                            </div>
                        )}

                        <div>
                            <Label className="co-label">Notes</Label>
                            <Textarea
                                value={ho.notes || ""}
                                onChange={(e) => patchHomeOffice({ notes: e.target.value })}
                                placeholder="Anything your accountant should know? (eg. moved house mid-year)"
                                disabled={isReadOnly}
                                data-testid="ho-notes"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* MOTOR VEHICLE */}
            <div className="co-card co-card-pad" data-testid="mv-section">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 grid place-items-center rounded-full bg-sky-50 text-sky-700"><Car className="h-4.5 w-4.5" /></div>
                        <div>
                            <div className="font-display text-lg font-medium">Motor vehicle claim</div>
                            <div className="text-xs text-[#5E636E]">IRD IS 22/04 — logbook required if business use exceeds 25%</div>
                        </div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                            checked={!!mv.has_vehicle_claim}
                            onCheckedChange={(v) => {
                                patchMotor({
                                    has_vehicle_claim: !!v,
                                    vehicles: v && !(mv.vehicles || []).length ? [blankVehicle()] : (mv.vehicles || []),
                                });
                            }}
                            disabled={isReadOnly}
                            data-testid="mv-toggle"
                        />
                        I&apos;m claiming vehicle expenses
                    </label>
                </div>

                {mv.has_vehicle_claim && (
                    <div className="mt-5 space-y-4">
                        {(mv.vehicles || []).map((v, idx) => (
                            <VehicleCard
                                key={idx}
                                v={v}
                                idx={idx}
                                isReadOnly={isReadOnly}
                                overThreshold={overLogbookThreshold(v)}
                                onPatch={(p) => patchVehicle(idx, p)}
                                onRemove={() => removeVehicle(idx)}
                            />
                        ))}
                        {!isReadOnly && (
                            <Button variant="outline" size="sm" onClick={addVehicle} data-testid="mv-add-vehicle">+ Add another vehicle</Button>                        )}
                    </div>
                )}
            </div>

            {/* DECLARATION + SUBMIT */}
            <div className="co-card co-card-pad" data-testid="declaration-section">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 grid place-items-center rounded-full bg-zinc-100 text-[#171717]"><ShieldCheck className="h-4.5 w-4.5" /></div>
                    <div>
                        <div className="font-display text-lg font-medium">Declaration</div>
                        <div className="text-xs text-[#5E636E]">Confirm the information you&apos;ve entered is correct.</div>
                    </div>
                </div>
                <div className="mt-4 space-y-3">
                    <label className="flex items-start gap-3 text-sm cursor-pointer">
                        <Checkbox
                            className="mt-0.5"
                            checked={!!record.declaration_accepted}
                            onCheckedChange={(v) => setRecord((r) => ({ ...r, declaration_accepted: !!v }))}
                            disabled={isReadOnly}
                            data-testid="declaration-checkbox"
                        />
                        <span>
                            I confirm that the home-office and motor-vehicle information above is accurate to the best of my knowledge,
                            and that I will retain the supporting records (rates notices, power bills, vehicle logbooks, etc.) for at
                            least 7 years as required by NZ IRD.
                        </span>
                    </label>
                    {record.declaration_accepted && !isReadOnly && (
                        <div>
                            <Label className="co-label">Your full name</Label>
                            <Input
                                value={record.declaration_name || user?.name || ""}
                                onChange={(e) => setRecord((r) => ({ ...r, declaration_name: e.target.value }))}
                                placeholder="Type your full name as signature"
                                data-testid="declaration-name"
                            />
                        </div>
                    )}
                </div>
            </div>

            {!isReadOnly && (
                <div className="sticky bottom-2 z-10 flex flex-wrap items-center gap-3 justify-end bg-white/85 backdrop-blur border border-[#E4E4E7] rounded-xl p-3">
                    {logbookGap && (
                        <div className="mr-auto text-xs text-rose-700" data-testid="compliance-logbook-gap">
                            <AlertTriangle className="inline h-3.5 w-3.5 mr-1" /> {logbookGap}
                        </div>
                    )}
                    <Button variant="outline" onClick={save} disabled={saving || submitting} data-testid="compliance-save">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}
                    </Button>
                    <Button
                        onClick={submitRecord}
                        disabled={submitting || saving || !record.declaration_accepted || !!logbookGap}
                        className="bg-[#171717] hover:bg-[#2E2E2E] text-white"
                        data-testid="compliance-submit"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit to accountant"}
                    </Button>
                </div>
            )}
        </div>
    );
}


function VehicleCard({ v, idx, isReadOnly, overThreshold, onPatch, onRemove }) {
    return (
        <div className="rounded-lg border border-[#E4E4E7] bg-white p-4" data-testid={`mv-vehicle-${idx}`}>
            <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Vehicle {idx + 1}{v.registration ? ` — ${v.registration}` : ""}</div>
                {!isReadOnly && (
                    <button type="button" onClick={onRemove} className="text-xs text-[#5E636E] hover:text-rose-700" data-testid={`mv-remove-${idx}`}>Remove</button>
                )}
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
                <div>
                    <Label className="co-label">Registration</Label>
                    <Input value={v.registration || ""} onChange={(e) => onPatch({ registration: e.target.value.toUpperCase() })} placeholder="ABC123" disabled={isReadOnly} data-testid={`mv-rego-${idx}`} />
                </div>
                <div>
                    <Label className="co-label">Make</Label>
                    <Input value={v.make || ""} onChange={(e) => onPatch({ make: e.target.value })} placeholder="Toyota" disabled={isReadOnly} data-testid={`mv-make-${idx}`} />
                </div>
                <div>
                    <Label className="co-label">Model</Label>
                    <Input value={v.model || ""} onChange={(e) => onPatch({ model: e.target.value })} placeholder="Hilux" disabled={isReadOnly} data-testid={`mv-model-${idx}`} />
                </div>
                <div>
                    <Label className="co-label">Business use %</Label>
                    <Input
                        type="number" min="0" max="100" step="1"
                        value={v.business_use_percent ?? 0}
                        onChange={(e) => onPatch({ business_use_percent: Number(e.target.value || 0) })}
                        disabled={isReadOnly}
                        data-testid={`mv-pct-${idx}`}
                    />
                </div>
                <div>
                    <Label className="co-label">Method</Label>
                    <Select value={v.method || "km_rate"} onValueChange={(val) => onPatch({ method: val })} disabled={isReadOnly}>
                        <SelectTrigger data-testid={`mv-method-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {Object.entries(METHOD_LABELS).map(([k, lab]) => (
                                <SelectItem key={k} value={k}>{lab}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="co-label">Total km / year</Label>
                    <Input type="number" min="0" value={v.total_km_annual ?? ""} onChange={(e) => onPatch({ total_km_annual: e.target.value === "" ? null : Number(e.target.value) })} disabled={isReadOnly} data-testid={`mv-total-km-${idx}`} />
                </div>
                <div className="sm:col-span-3">
                    <Label className="co-label">Business km / year</Label>
                    <Input type="number" min="0" value={v.business_km_annual ?? ""} onChange={(e) => onPatch({ business_km_annual: e.target.value === "" ? null : Number(e.target.value) })} disabled={isReadOnly} data-testid={`mv-business-km-${idx}`} />
                </div>
            </div>

            {overThreshold && (
                <div className="mt-4 rounded-md border border-rose-300 bg-rose-50 p-3" data-testid={`mv-logbook-warning-${idx}`}>
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-rose-700 mt-0.5 shrink-0" />
                        <div className="text-sm text-rose-900">
                            <strong>Business use exceeds 25%</strong> — IRD requires a logbook covering a representative
                            90 consecutive days. The logbook is valid for 3 years.
                        </div>
                    </div>
                    {v.method === "logbook" ? (
                        <div className="mt-3 grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="co-label">Logbook period start</Label>
                                <Input
                                    type="date"
                                    value={v.logbook_period_start || ""}
                                    onChange={(e) => onPatch({ logbook_period_start: e.target.value })}
                                    disabled={isReadOnly}
                                    data-testid={`mv-logbook-start-${idx}`}
                                />
                            </div>
                            <div>
                                <Label className="co-label">Logbook period end</Label>
                                <Input
                                    type="date"
                                    value={v.logbook_period_end || ""}
                                    onChange={(e) => onPatch({ logbook_period_end: e.target.value })}
                                    disabled={isReadOnly}
                                    data-testid={`mv-logbook-end-${idx}`}
                                />
                            </div>
                        </div>
                    ) : (
                        <p className="mt-2 text-xs text-rose-800">You&apos;ve picked <strong>{METHOD_LABELS[v.method] || v.method}</strong> instead of a logbook — make sure you&apos;ve discussed this with your accountant.</p>
                    )}
                </div>
            )}

            <div className="mt-3">
                <Label className="co-label">Notes</Label>
                <Textarea
                    value={v.notes || ""}
                    onChange={(e) => onPatch({ notes: e.target.value })}
                    placeholder="Anything else? (eg. purchased mid-year, vehicle changed)"
                    disabled={isReadOnly}
                    data-testid={`mv-notes-${idx}`}
                />
            </div>
        </div>
    );
}
